import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  MenuItem,
} from "@mui/material";
import { Formik, Form } from "formik";
import { CreateOutlined } from "@mui/icons-material";
import "./lead-details-information-form.scss";

import * as Yup from "yup";
import { updateLeadDataById } from "../../../../api/provider-section/provider-service-management";
import NewPhoneNumberComponent from "../../../../common/components/new-phone-input-component/new-phone-input-component";
import {
  extractCountryCodeFromPhone,
  joiEmailSchema,
  normalizePhoneNumber,
} from "../../../../utils/utils";
import { AlertComponent } from "../../../../common/components";
interface LeadDetailsInformationFormProps {
  data: any;
  editMode: boolean;
  innerRef?: React.Ref<any>;
  infoItems: { label: string; value: string }[];
  fetchLeadDetails: () => void;
  updateLeadNameInBreadcrumb?: (newName: string) => void;
}
interface LeadInformationFormValues {
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  interestedProduct: string;
  status: string;
  leadSource: string;
}
const leadStatuses = ["New Lead", "In Progress", "Closed-Won", "Closed-Lost"];
const interestedProducts = ["Immediate Need", "Preneed", "Preneed Planner"];
const LeadDetailsInformationForm: React.FC<LeadDetailsInformationFormProps> = ({
  data,
  innerRef,
  infoItems,
  fetchLeadDetails,
  updateLeadNameInBreadcrumb,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const [countryCode, setCountryCode] = useState(
    extractCountryCodeFromPhone(data?.phone) || "+1",
  );
  const handleSubmit = async (values: any) => {
    try {
      const phoneWithCountryCode = values.phoneNumber.startsWith("+")
        ? values.phoneNumber
        : values.phoneNumber
          ? `${countryCode}${values.phoneNumber}`
          : null;
      // let interestedProductPayload = values.interestedProduct;
      // if (values.interestedProduct === "Immediate Need") {
      //   interestedProductPayload = "at_need";
      // } else if (values.interestedProduct === "Preneed") {
      //   interestedProductPayload = "pre_need";
      // } else {
      //   interestedProductPayload = values.interestedProduct;
      // }
      const response = await updateLeadDataById(data.id, {
        fullName: values.fullName,
        primaryEmail: values.emailAddress,
        primaryPhoneNumber: phoneWithCountryCode,
        // interestedProduct: interestedProductPayload,
        leadStatus: values.status,
        address: data.address,
      });
      setEditMode(false);
      setAlertType("success");
      setAlertMessage(
        response?.data?.message || "Lead details updated successfully",
      );
      setAlertOpen(true);
      if (updateLeadNameInBreadcrumb) {
        updateLeadNameInBreadcrumb(values.fullName);
      }
      fetchLeadDetails();
    } catch (error) {
      console.error("Error updating lead details:", error);
      setAlertType("error");
      setAlertMessage("Failed to update lead details");
      setAlertOpen(true);
    }
  };
  const formInitialValues: LeadInformationFormValues = {
    fullName: data?.name || "",
    emailAddress: data?.email || "",
    phoneNumber: normalizePhoneNumber(data?.phone) || "",
    interestedProduct: data?.needType || "",
    status: data?.leadStatus || "",
    leadSource: data?.leadSource || "",
  };
  const allowAlphabetsOnly =
    (field: string, setFieldValue: any) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFieldValue(field, value);
      }
    };
  const validationSchema = Yup.object({
    fullName: Yup.string().required("Full Name is required"),
    emailAddress: Yup.string()
      .email("Please enter a valid email address")
      .max(50, "Maximum 50 characters allowed")
      .required("Email is required")
      .test(
        "joi-email-check",
        "Please enter a valid email address",
        function (value) {
          if (!value) return true;
          const { error } = joiEmailSchema.validate(value);
          return !error;
        },
      ),
    phoneNumber: Yup.string().optional().nullable(),
    status: Yup.string().nullable().optional(),
    // interestedProduct: Yup.string().optional().nullable(),
    leadSource: Yup.string().optional().nullable(),
  });
  return (
    <>
      <Box className="lead-details-information-form">
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          innerRef={innerRef}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            resetForm,
            submitForm,
          }) => (
            <>
              <Paper elevation={0} className="lead-details-form-section">
                <Box className="lead-details-section-header">
                  <Typography
                    variant="h6"
                    className="lead-details-section-title"
                  >
                    Lead Information
                  </Typography>
                  {!editMode ? (
                    <div className="lead-details-section-edit-section">
                      <CreateOutlined
                        onClick={() => {
                          if (
                            data?.leadStatus !== "Closed-Won" &&
                            data?.leadStatus !== "Closed-Lost"
                          ) {
                            setEditMode(true);
                          }
                        }}
                        className={`lead-details-section-edit-icon ${
                          data?.leadStatus === "Closed-Won" ||
                          data?.leadStatus === "Closed-Lost"
                            ? "disabled"
                            : ""
                        }`}
                        fontSize="small"
                        style={{
                          cursor:
                            data?.leadStatus === "Closed-Won" ||
                            data?.leadStatus === "Closed-Lost"
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            data?.leadStatus === "Closed-Won" ||
                            data?.leadStatus === "Closed-Lost"
                              ? 0.5
                              : 1,
                        }}
                      />
                    </div>
                  ) : (
                    <Box className="lead-details-section-actions">
                      <Button
                        variant="outlined"
                        className="lead-details-section-cancel-btn"
                        onClick={() => {
                          resetForm();
                          setEditMode(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          submitForm();
                        }}
                        className="lead-details-section-save-btn"
                      >
                        Save
                      </Button>
                    </Box>
                  )}
                </Box>
                <Form
                  id="lead-details-form"
                  className="lead-details-information-form__form"
                >
                  <Box className="lead-details-information-form__row">
                    <Box className="lead-details-information-row">
                      <Box className="lead-details-information-form__field lead-details-information-form__half">
                        {editMode ? (
                          <>
                            <Typography className="lead-details-information-form__label">
                              Full Name{" "}
                              <span className="lead-details-information-form__required">
                                *
                              </span>
                            </Typography>
                            <TextField
                              name="fullName"
                              value={values.fullName}
                              onChange={allowAlphabetsOnly(
                                "fullName",
                                setFieldValue,
                              )}
                              onBlur={handleBlur}
                              error={
                                touched.fullName && Boolean(errors.fullName)
                              }
                              helperText={touched.fullName && errors.fullName}
                              fullWidth
                              size="small"
                              placeholder="Enter Full Name"
                              className="lead-details-information-form__input"
                            />
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              className="lead-details__item-label"
                            >
                              {"Full Name"}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="lead-details__item-value"
                            >
                              {infoItems.find(
                                (item) => item.label === "Full Name",
                              )?.value?.trim() || "-"}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box
                        className="lead-details-information-form__field lead-details-information-form__half"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            height: "50px",
                            width: "90% !important",
                          },
                          "& .MuiInputBase-root": {
                            fontSize: "14px",
                            height: "40px",
                            padding: "12px 6px",
                          },
                          "& input": { color: "#104145" },
                        }}
                      >
                        {editMode ? (
                          <>
                            <Typography className="lead-details-information-form__label">
                              Phone Number{" "}
                            </Typography>
                            <NewPhoneNumberComponent
                              value={values.phoneNumber}
                              onChange={(value: string) =>
                                setFieldValue("phoneNumber", value)
                              }
                              countryCode={countryCode}
                              onCountryChange={setCountryCode}
                              placeholder="Enter Phone Number"
                              error={
                                touched.phoneNumber &&
                                Boolean(errors.phoneNumber)
                              }
                              showHelper={false}
                            />
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              className="lead-details__item-label"
                            >
                              {"Phone Number"}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="lead-details__item-value"
                            >
                              {infoItems.find(
                                (item) => item.label === "Phone Number",
                              )?.value || "-"}
                            </Typography>
                          </>
                        )}
                      </Box>

                      <Box className="lead-details-information-form__field lead-details-information-form__half">
                        {editMode ? (
                          <>
                            <Typography className="lead-details-information-form__label">
                              Status{" "}
                            </Typography>
                            <TextField
                              fullWidth
                              select
                              id="status"
                              name="status"
                              value={values.status}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.status && Boolean(errors.status)}
                              helperText={
                                (touched.status && (errors.status as string)) ||
                                ""
                              }
                              className="lead-details-information-form__input"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "40px",
                                },
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected: any) => {
                                  if (!selected) {
                                    return (
                                      <span style={{ color: "#9ca3af" }}>
                                        Select Status
                                      </span>
                                    );
                                  }
                                  return selected as string;
                                },
                              }}
                            >
                              {leadStatuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </TextField>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              className="lead-details__item-label"
                            >
                              {"Status"}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="lead-details__item-value"
                            >
                              {infoItems.find((item) => item.label === "Status")
                                ?.value || "-"}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Box className="lead-details-information-row">
                      <Box className="lead-details-information-form__field lead-details-information-form__half">
                        {editMode ? (
                          <>
                            <Typography className="lead-details-information-form__label">
                              Email Address{" "}
                              <span className="lead-details-information-form__required">
                                *
                              </span>
                            </Typography>
                            <TextField
                              name="emailAddress"
                              value={values.emailAddress}
                              onChange={(e) =>
                                setFieldValue("emailAddress", e.target.value)
                              }
                              onBlur={handleBlur}
                              error={
                                touched.emailAddress &&
                                Boolean(errors.emailAddress)
                              }
                              helperText={
                                touched.emailAddress && errors.emailAddress
                              }
                              inputProps={{ maxLength: 50 }}
                              fullWidth
                              size="small"
                              placeholder="Enter Email Address"
                              className="lead-details-information-form__input"
                            />
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              className="lead-details__item-label"
                            >
                              {"Email Address"}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="lead-details__item-value"
                            >
                              {infoItems.find(
                                (item) => item.label === "Email Address",
                              )?.value || "-"}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box className="lead-details-information-form__field lead-details-information-form__half">
                        {editMode ? (
                          <>
                            <Typography className="lead-details-information-form__label">
                              Interested Product{" "}
                            </Typography>
                            <TextField
                              fullWidth
                              select
                              id="interestedProduct"
                              name="interestedProduct"
                              value={values.interestedProduct}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.interestedProduct &&
                                Boolean(errors.interestedProduct)
                              }
                              helperText={
                                (touched.interestedProduct &&
                                  (errors.interestedProduct as string)) ||
                                ""
                              }
                              className="lead-details-information-form__input"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "40px",
                                  cursor: "not-allowed",
                                },
                                "& .MuiSelect-select": {
                                  cursor: "not-allowed",
                                },
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected: any) => {
                                  if (!selected) {
                                    return (
                                      <span style={{ color: "#9ca3af" }}>
                                        Select Interested Product
                                      </span>
                                    );
                                  }
                                  const normalized = (selected || "")
                                    .toLowerCase()
                                    .replace(/[_-]/g, "-");
                                  if (
                                    [
                                      "at-need",
                                      "atneed",
                                      "at_need",
                                      "atneed",
                                      "immediate-need",
                                      "immediate need",
                                    ].some(
                                      (v) =>
                                        normalized.replace(/-/g, " ") ===
                                        v.replace(/-/g, " "),
                                    )
                                  ) {
                                    return "Immediate Need";
                                  }
                                  if (
                                    [
                                      "pre-need",
                                      "preneed",
                                      "pre_need",
                                      "preneed",
                                    ].some(
                                      (v) =>
                                        normalized.replace(/-/g, " ") ===
                                        v.replace(/-/g, " "),
                                    )
                                  ) {
                                    return "Preneed";
                                  }
                                  return selected as string;
                                },
                              }}
                              disabled={true}
                            >
                              {interestedProducts.map((interestedProduct) => {
                                let displayValue = interestedProduct;
                                const normalized = interestedProduct
                                  .toLowerCase()
                                  .replace(/[_-]/g, "-");
                                if (
                                  [
                                    "at-need",
                                    "atneed",
                                    "at_need",
                                    "atneed",
                                    "immediate-need",
                                    "immediate need",
                                  ].some(
                                    (v) =>
                                      normalized.replace(/-/g, " ") ===
                                      v.replace(/-/g, " "),
                                  )
                                ) {
                                  displayValue = "Immediate Need";
                                } else if (
                                  [
                                    "pre-need",
                                    "preneed",
                                    "pre_need",
                                    "preneed",
                                  ].some(
                                    (v) =>
                                      normalized.replace(/-/g, " ") ===
                                      v.replace(/-/g, " "),
                                  )
                                ) {
                                  displayValue = "Preneed";
                                }
                                return (
                                  <MenuItem
                                    key={interestedProduct}
                                    value={interestedProduct}
                                  >
                                    {displayValue}
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              className="lead-details__item-label"
                            >
                              {"Interested Product"}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="lead-details__item-value"
                            >
                              {(() => {
                                const value = infoItems.find(
                                  (item) => item.label === "Interested Product",
                                )?.value;
                                if (!value) return "-";
                                const normalized = value
                                  .toLowerCase()
                                  .replace(/[_-]/g, "-");
                                if (
                                  ["at-need", "At-Need", "at_need", "immediate need", "Immediate Need"].some(
                                    (v) =>
                                      normalized.replace(/[-_]/g, " ") ===
                                      v.toLowerCase().replace(/[-_]/g, " "),
                                  )
                                ) {
                                  return "Immediate Need";
                                }
                                if (
                                  ["pre-need", "Pre-Need", "pre_need", "preneed", "Preneed"].some(
                                    (v) =>
                                      normalized.replace(/[-_]/g, " ") ===
                                      v.toLowerCase().replace(/[-_]/g, " "),
                                  )
                                ) {
                                  return "Preneed";
                                }
                                return value;
                              })()}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box className="lead-details-information-form__field lead-details-information-form__half">
                        {editMode ? (
                          <>
                            <Typography className="lead-details-information-form__label">
                              Lead Source{" "}
                            </Typography>
                            <TextField
                              fullWidth
                              select
                              id="leadSource"
                              name="leadSource"
                              value={values.leadSource}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.leadSource && Boolean(errors.leadSource)
                              }
                              helperText={
                                (touched.leadSource &&
                                  (errors.leadSource as string)) ||
                                ""
                              }
                              className="lead-details-information-form__input"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "40px",
                                  cursor: "not-allowed",
                                },
                                "& .MuiSelect-select": {
                                  cursor: "not-allowed",
                                },
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected: any) => {
                                  if (!selected) {
                                    return (
                                      <span style={{ color: "#9ca3af" }}>
                                        Select Lead Source
                                      </span>
                                    );
                                  }
                                  return selected as string;
                                },
                              }}
                              disabled={true}
                            >
                              <MenuItem
                                key={values.leadSource}
                                value={values.leadSource}
                              >
                                {values.leadSource}
                              </MenuItem>
                            </TextField>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              className="lead-details__item-label"
                            >
                              {"Lead Source"}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="lead-details__item-value"
                            >
                              {infoItems.find(
                                (item) => item.label === "Lead Source",
                              )?.value || "-"}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Form>
              </Paper>
            </>
          )}
        </Formik>
      </Box>
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};
export default LeadDetailsInformationForm;
